/**
 * AP-13: Hinweis-Banner für Ref-Listen- und Edit-Seiten.
 *
 * Zeigt, ob die jeweilige Entität in `INTERNAL` / `MANUAL` / `EXTERNAL_API`
 * verwaltet wird, und begründet bei `EXTERNAL_API` den Read-only-Modus.
 * AP-35: `PROJECT_MGMT` ist ebenfalls nur lesbar — die Quelle ist das eigene
 * Projektmanagement.
 */
import { useTerminology } from '@/lib/useTerminology';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import type { RefEntityKind } from '../types/time-master-data.types';

export interface ModeBannerProps {
  kind: RefEntityKind;
  className?: string;
}

export function ModeBanner({ kind, className = '' }: ModeBannerProps) {
  const { mode, isLoading, canEdit, readOnlyReason } = useDataSourceMode(kind);
  const terms = useTerminology();

  if (isLoading || mode == null) return null;
  if (canEdit && mode === 'INTERNAL') return null;

  return (
    <div
      className={`rounded-md border px-3 py-2 text-xs ${className} ${
        canEdit
          ? 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
          : 'border-red-400 bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200'
      }`}
      role="status"
    >
      <strong className="uppercase tracking-wide mr-2">{mode === 'EXTERNAL_API'
          ? 'Aus deinem anderen Programm'
          : mode === 'PROJECT_MGMT'
            ? `Quelle: ${terms.management}`
            : 'Manueller Modus'}</strong>
      {canEdit
        ? 'Du kannst diese Daten hier pflegen. In der Buchungsmaske greift die Zuordnung aber noch nicht.'
        : readOnlyReason}
    </div>
  );
}
